// ============================================================
//  PENDING CERTIFICATION PAGE — ADMIN DASHBOARD
//  Path: src/pages/PendingCertificationPage.tsx
//
//  Global review queue for experts awaiting the admin's blanket
//  "Certify" grant (Expert.isCertified — see admin.service.js's
//  certifyExpert). The backend route, controller, and service
//  (listExpertsPendingCertification) already existed; nothing in
//  the dashboard ever called it, so this queue was invisible —
//  certifyExpert could only ever be reached one expert at a time,
//  from inside ExpertDetailPage, with no list of who actually
//  needs it.
//
//  Same shape as VehicleVerificationsPage: a table, an action
//  button per row, and a "View" link into ExpertDetailPage for
//  the full picture (documents, categories, etc.) before deciding.
//  Unlike vehicle review, certifying has no rejection-reason flow —
//  it's a single admin judgment call, not a per-document decision —
//  so there's one action, not two.
//
//  Access: super_admin, admin only (certifyExpertApi is gated the
//  same way on the backend — see admin.routes.js's
//  PATCH /experts/:id/certify).
// ============================================================

import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { listExpertsPendingCertification, certifyExpert } from '../services/admin.service'
import { Expert } from '../types'
import { useAuth } from '../context/AuthContext'

// FIX (review pass):
//  - Certify asks for confirmation first — it's customer-visible across
//    every category the expert offers, and was one stray click.
//  - Documents column shows how many are actually verified / rejected /
//    still pending (the backend already sends each one's state), and a
//    Status column shows the expert's own status. certifyExpert doesn't
//    refuse a rejected or suspended expert, so the admin needs to see it.
//  - customer_service can open this queue (GET is allowed for them) but
//    PATCH /certify is super_admin/admin only — they now get a View Only
//    badge instead of a Certify button that always fails.
//  - A load failure no longer also says "No experts pending
//    certification", and a certify failure shows the server's reason.

const STATUS_COLORS: Record<string, string> = {
  pending:          'bg-yellow-600/20 text-yellow-400',
  under_review:     'bg-blue-600/20 text-blue-400',
  background_check: 'bg-purple-600/20 text-purple-400',
  approved:         'bg-green-600/20 text-green-400',
  rejected:         'bg-red-600/20 text-red-400',
  suspended:        'bg-gray-600/20 text-gray-400',
}

// Counts per review state. rejectionReason isn't on the shared
// ExpertDocument type, but the backend includes it on every row.
const docCounts = (ex: Expert) => {
  const docs = ex.documents ?? []
  const verified = docs.filter((d) => d.isVerified).length
  const rejected = docs.filter((d) => !d.isVerified && !!(d as { rejectionReason?: string | null }).rejectionReason).length
  return { total: docs.length, verified, rejected, pending: docs.length - verified - rejected }
}

const PendingCertificationPage: React.FC = () => {
  const { user } = useAuth()
  const canModify = !!(user?.roles.includes('super_admin') || user?.roles.includes('admin'))

  const [experts, setExperts]   = useState<Expert[]>([])
  const [loading, setLoading]   = useState(true)
  const [actingId, setActingId] = useState<string | null>(null)
  const [error, setError]       = useState('')
  const [loadFailed, setLoadFailed] = useState(false)

  const fetchExperts = async () => {
    setLoading(true)
    setError('')
    setLoadFailed(false)
    try {
      const res = await listExpertsPendingCertification()
      setExperts(res.data.data)
    } catch (err: any) {
      console.error('Failed to fetch experts pending certification:', err)
      setLoadFailed(true)
      setError(err?.response?.data?.message || (err?.isNetworkError
        ? 'Could not reach the server — check your connection and try again.'
        : 'Failed to load pending certifications'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchExperts() }, [])

  // Grants isCertified across the expert AND every one of their
  // categories in one transaction — see the header comment on
  // certifyExpert in admin.service.js for why that propagation
  // matters (a customer-facing certifiedOnly filter reads the
  // category-level flag, not the expert-level one).
  const handleCertify = async (ex: Expert) => {
    if (!canModify) return
    const name = ex.user ? `${ex.user.firstName} ${ex.user.lastName}` : 'this expert'
    const { total, verified } = docCounts(ex)
    const warnings = [
      ex.status !== 'approved' ? `Their status is "${ex.status.replace(/_/g, ' ')}", not approved.` : '',
      verified < total ? `Only ${verified} of ${total} documents are verified.` : '',
    ].filter(Boolean).join('\n')
    if (!window.confirm(
      `Certify ${name}?\n\nThe certified badge applies to their whole profile and every category they offer, and customers will see it.` +
      (warnings ? `\n\n⚠ ${warnings}` : '')
    )) return

    setError('')
    setActingId(ex.id)
    try {
      await certifyExpert(ex.id, true)
      // Certifying removes this expert from the pending queue —
      // refetch rather than filter locally, so the count and list
      // stay accurate if something else changed server-side too.
      fetchExperts()
    } catch (err: any) {
      console.error('Failed to certify expert:', err)
      setError(err?.response?.data?.message || 'Failed to certify — please try again')
    } finally {
      setActingId(null)
    }
  }

  return (
    <div>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Pending Certification</h1>
          <p className="text-gray-400 text-sm mt-1">
            Experts with at least one submitted document who have not yet been granted certified status.
            Certifying applies across the expert's entire profile and every category they offer.
          </p>
        </div>
        {!canModify && (
          <span className="px-3 py-1.5 text-xs rounded-full bg-yellow-600/20 text-yellow-400 shrink-0">View Only</span>
        )}
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 flex items-center justify-between gap-3">
          <p className="text-red-400 text-sm">{error}</p>
          {loadFailed && (
            <button onClick={() => fetchExperts()}
              className="px-3 py-1.5 text-xs bg-surface-tertiary text-gray-300 rounded-lg hover:bg-border-light transition-colors shrink-0">
              Try again
            </button>
          )}
        </div>
      )}

      <div className="bg-surface-card border border-border-light rounded-2xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border-light">
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Expert</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Contact</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Status</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Documents</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Applied</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-8 text-gray-400">Loading...</td></tr>
            ) : loadFailed ? (
              <tr><td colSpan={6} className="text-center py-8 text-gray-500">—</td></tr>
            ) : experts.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-8 text-gray-400">No experts pending certification</td></tr>
            ) : (
              experts.map((ex) => { const counts = docCounts(ex); return (
                <tr key={ex.id} className="border-b border-border-light hover:bg-surface-tertiary/50 transition-colors">
                  <td className="px-5 py-4">
                    <Link to={`/experts/${ex.id}`} className="text-sm font-medium text-brand-400 hover:text-brand-300">
                      {ex.user ? `${ex.user.firstName} ${ex.user.lastName}` : 'Unknown user'}
                    </Link>
                  </td>
                  <td className="px-5 py-4">
                    <p className="text-sm text-gray-300">{ex.user?.email || '—'}</p>
                    <p className="text-xs text-gray-500">{ex.user?.phone || '—'}</p>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${STATUS_COLORS[ex.status] || 'bg-gray-600/20 text-gray-400'}`}>
                      {ex.status.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-sm">
                    <p className="text-gray-300">{counts.total} submitted</p>
                    <p className="text-xs mt-0.5">
                      <span className="text-green-400">{counts.verified} verified</span>
                      {counts.pending > 0 && <span className="text-yellow-400"> · {counts.pending} pending</span>}
                      {counts.rejected > 0 && <span className="text-red-400"> · {counts.rejected} rejected</span>}
                    </p>
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-400">
                    {ex.createdAt ? new Date(ex.createdAt).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex gap-2">
                      {canModify && (
                        <button
                          onClick={() => handleCertify(ex)}
                          disabled={actingId === ex.id}
                          className="px-3 py-1.5 text-xs bg-green-500/10 text-green-400 rounded-lg hover:bg-green-500/20 transition-colors disabled:opacity-50"
                        >
                          {actingId === ex.id ? 'Certifying...' : 'Certify'}
                        </button>
                      )}
                      <Link
                        to={`/experts/${ex.id}`}
                        className="px-3 py-1.5 text-xs bg-surface-tertiary text-gray-300 rounded-lg hover:bg-border-light transition-colors"
                      >
                        View
                      </Link>
                    </div>
                  </td>
                </tr>
              ) })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default PendingCertificationPage